const SIREN_LENGTH = 9;
const SIRET_LENGTH = 14;
const DOUBLED_DIGIT_THRESHOLD = 9;

export type OrganizationIdentifierKind = 'siren' | 'siret';

const SIREN_GROUP_LENGTHS = [3, 3, 3] as const;
const SIRET_GROUP_LENGTHS = [3, 3, 3, 5] as const;

export function normalizeOrganizationIdentifierValue(value: string): string {
  return value.replace(/\D/g, '');
}

function isValidLuhnChecksum(digits: string): boolean {
  const checksum = digits
    .split('')
    .reverse()
    .reduce((sum, digit, index) => {
      const value = Number(digit);
      const doubled = index % 2 === 1 ? value * 2 : value;
      return (
        sum +
        (doubled > DOUBLED_DIGIT_THRESHOLD
          ? doubled - DOUBLED_DIGIT_THRESHOLD
          : doubled)
      );
    }, 0);

  return checksum % 10 === 0;
}

export function isValidSiren(siren: string): boolean {
  return siren.length === SIREN_LENGTH && isValidLuhnChecksum(siren);
}

export function isValidSiret(siret: string): boolean {
  return siret.length === SIRET_LENGTH && isValidLuhnChecksum(siret);
}

export function isValidOrganizationIdentifierValue(value: string): boolean {
  if (value.length === SIREN_LENGTH) {
    return isValidSiren(value);
  }

  if (value.length === SIRET_LENGTH) {
    return isValidSiret(value);
  }

  return false;
}

export function deriveSirenFromSiret(siret: string): string {
  return siret.slice(0, SIREN_LENGTH);
}

export function getOrganizationIdentifierKind(
  normalizedValue: string,
): OrganizationIdentifierKind | null {
  if (normalizedValue.length === SIREN_LENGTH) {
    return 'siren';
  }

  if (normalizedValue.length === SIRET_LENGTH) {
    return 'siret';
  }

  return null;
}

function formatWithGroupLengths(
  rawValue: string,
  maxDigits: number,
  groupLengths: readonly number[],
): string {
  const digits = rawValue.replace(/\D/g, '').slice(0, maxDigits);

  const groups: string[] = [];
  let cursor = 0;

  for (const groupLength of groupLengths) {
    const group = digits.slice(cursor, cursor + groupLength);
    if (!group) {
      break;
    }
    groups.push(group);
    cursor += groupLength;
  }

  return groups.join(' ');
}

export function formatSiren(rawValue: string): string {
  return formatWithGroupLengths(rawValue, SIREN_LENGTH, SIREN_GROUP_LENGTHS);
}

export function formatSiret(rawValue: string): string {
  return formatWithGroupLengths(rawValue, SIRET_LENGTH, SIRET_GROUP_LENGTHS);
}

export function formatOrganizationIdentifierInput(rawValue: string): string {
  const digits = rawValue.replace(/\D/g, '').slice(0, SIRET_LENGTH);

  return digits.length > SIREN_LENGTH
    ? formatSiret(digits)
    : formatSiren(digits);
}
